const CANDIDATE_LOADING_SETTING_KEYS = {
  title: "candidate_loading_title",
  description: "candidate_loading_description",
  icon: "candidate_loading_icon",
  tile: "candidate_loading_tile",
  spin: "candidate_loading_spin",
  pulse: "candidate_loading_pulse",
  ping: "candidate_loading_ping",
} as const;

const DEFAULT_CANDIDATE_LOADING_INDICATOR = {
  title: "Hang tight, we're loading…",
  description: "Setting things up for you…",
  icon: "lucide:loader-circle",
  tile: true,
  spin: true,
  pulse: true,
  ping: true,
};

function hasSetting(settings: Record<string, unknown>, key: string) {
  return Object.prototype.hasOwnProperty.call(settings, key);
}

function stringSetting(
  settings: Record<string, unknown>,
  key: string,
  fallback: string,
) {
  return hasSetting(settings, key) ? String(settings[key] ?? "") : fallback;
}

function booleanSetting(
  settings: Record<string, unknown>,
  key: string,
  fallback: boolean,
) {
  if (!hasSetting(settings, key)) return fallback;

  const value = settings[key];
  if (value === true || value === "true") return true;
  if (value === false || value === "false") return false;
  return fallback;
}

export function useCandidateLoadingIndicator() {
  const { site } = useSite();

  const configuration = computed(() => {
    const settings = (site.value?.settings ?? {}) as Record<string, unknown>;
    const title = stringSetting(
      settings,
      CANDIDATE_LOADING_SETTING_KEYS.title,
      DEFAULT_CANDIDATE_LOADING_INDICATOR.title,
    );
    const description = stringSetting(
      settings,
      CANDIDATE_LOADING_SETTING_KEYS.description,
      DEFAULT_CANDIDATE_LOADING_INDICATOR.description,
    );
    const icon = stringSetting(
      settings,
      CANDIDATE_LOADING_SETTING_KEYS.icon,
      DEFAULT_CANDIDATE_LOADING_INDICATOR.icon,
    );

    return {
      title,
      description,
      icon,
      tile: booleanSetting(
        settings,
        CANDIDATE_LOADING_SETTING_KEYS.tile,
        DEFAULT_CANDIDATE_LOADING_INDICATOR.tile,
      ),
      spin: booleanSetting(
        settings,
        CANDIDATE_LOADING_SETTING_KEYS.spin,
        DEFAULT_CANDIDATE_LOADING_INDICATOR.spin,
      ),
      pulse: booleanSetting(
        settings,
        CANDIDATE_LOADING_SETTING_KEYS.pulse,
        DEFAULT_CANDIDATE_LOADING_INDICATOR.pulse,
      ),
      ping: booleanSetting(
        settings,
        CANDIDATE_LOADING_SETTING_KEYS.ping,
        DEFAULT_CANDIDATE_LOADING_INDICATOR.ping,
      ),
      visible:
        title.trim().length > 0 ||
        description.trim().length > 0 ||
        icon.trim().length > 0,
    };
  });

  return {
    configuration,
  };
}
